import { create } from 'zustand';
import { apiService } from '@/services/api';
import type {
  BankAccount,
  BankAccountDeletePreview,
  BankAccountListResponse,
  ListParameters,
  BankAccountSearchFilter,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
  CreateConsentResponse,
  ConsentFinalizeResponse,
  CurrencyTotal,
  SibsAccount,
  SibsBalance,
  StoredConsent,
} from '@/types';

// Each currency added up on its own (never across), cards apart — the server's ordering, for the
// accounts read live from SIBS, which never go through it.
export function totalsByCurrency(accounts: BankAccount[]): CurrencyTotal[] {
  const byKey = new Map<string, CurrencyTotal>();
  for (const account of accounts) {
    const currency = account.currency?.trim().toUpperCase() || 'XXX';
    const isCard = account.type === 'Credit';
    const key = `${currency}|${isCard}`;
    const total = byKey.get(key) ?? { currency, balance: 0, accountCount: 0, isCard };
    total.balance += account.balance;
    total.accountCount += 1;
    byKey.set(key, total);
  }
  return [...byKey.values()].sort(
    (a, b) =>
      Number(a.isCard) - Number(b.isCard) ||
      b.accountCount - a.accountCount ||
      Math.abs(b.balance) - Math.abs(a.balance) ||
      (a.currency < b.currency ? -1 : a.currency > b.currency ? 1 : 0)
  );
}

// Helper to convert SIBS account to BankAccount
function sibsAccountToBankAccount(
  sibsAccount: SibsAccount,
  aspspCde: string,
  balances?: SibsBalance[]
): BankAccount {
  // Find the available balance or use the first balance
  const availableBalance = balances?.find(
    (b) => b.balanceType === 'expected' || b.balanceType === 'interimAvailable' || b.balanceType === 'closingBooked'
  );
  const balanceAmount = availableBalance
    ? parseFloat(availableBalance.balanceAmount.amount)
    : 0;
  const currency = availableBalance?.balanceAmount.currency || sibsAccount.currency || 'EUR';

  return {
    id: 0, // SIBS-fetched accounts don't have a database ID yet
    bankName: sibsAccount.name || aspspCde,
    bankCode: aspspCde,
    integrationType: 'Sibs', // SIBS-fetched accounts are OpenBanking by definition
    bankLogoUrl: `/api/banks/${aspspCde}/logo`,
    friendlyName: sibsAccount.name || sibsAccount.ownerName || sibsAccount.iban,
    iban: sibsAccount.iban,
    currency: currency,
    balance: balanceAmount,
    type: sibsAccount.cashAccountType || 'CACC',
    status: sibsAccount.status || 'enabled',
    lastSyncedAt: new Date().toISOString(),
    consentExpiresAt: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString(),
    isActive: true,
  };
}

interface BankAccountStore {
  // State
  accounts: BankAccount[];
  currentAccount: BankAccount | null;
  isLoading: boolean;
  error: string | null;
  listParameters: ListParameters;
  /** One total per currency, cards apart — never one sum across currencies. */
  balanceTotals: CurrencyTotal[];
  totalCount: number;
  sibsAccountsLoaded: boolean;
  syncingAccountIds: number[];
  /** Movements read so far by each running sync (account id → count). */
  syncProgress: Record<number, number>;

  // Actions
  fetchAccounts: () => Promise<void>;
  fetchAccountById: (id: number) => Promise<void>;
  fetchSibsAccounts: (consent: StoredConsent) => Promise<void>;
  fetchSibsAccountBalances: (aspspCde: string, consentId: string, accountId: string) => Promise<SibsBalance[]>;
  createAccount: (request: CreateBankAccountRequest) => Promise<CreateConsentResponse>;
  updateAccount: (id: number, request: UpdateBankAccountRequest) => Promise<void>;
  /** Counts for the delete dialog; null when they could not be loaded (the dialog still works). */
  getDeletePreview: (id: number) => Promise<BankAccountDeletePreview | null>;
  deleteAccount: (id: number, deleteMovements?: boolean) => Promise<void>;
  syncAccount: (id: number) => Promise<void>;
  initiateConsent: (id: number) => Promise<string | null>;
  finalizeConsent: (id: number) => Promise<ConsentFinalizeResponse | null>;
  removeConsent: (id: number) => Promise<void>;
  setSearchFilters: (filters: BankAccountSearchFilter) => void;
  setCurrentPage: (page: number) => void;
  setSortBy: (sortBy: string, ascending: boolean) => void;
  clearError: () => void;
  setCurrentAccount: (account: BankAccount | null) => void;
}

export const useBankAccountStore = create<BankAccountStore>((set, get) => ({
  // INITIAL STATE
  accounts: [],
  currentAccount: null,
  isLoading: false,
  error: null,
  balanceTotals: [],
  totalCount: 0,
  sibsAccountsLoaded: false,
  syncingAccountIds: [],
  syncProgress: {},
  listParameters: {
    currentPage: 1,
    pageSize: 20,
    sortByParameter: 'friendlyName',
    sortAscending: true,
    initial: true,
  },

  // ASYNC ACTIONS
  fetchAccounts: async () => {
    set({ isLoading: true, error: null });
    try {
      const params = get().listParameters;
      const queryParams: Record<string, unknown> = {
        page: params.currentPage,
        pageSize: params.pageSize,
        sortBy: params.sortByParameter,
        sortAscending: params.sortAscending,
      };

      // Add search filters if present
      if (params.searchFilters) {
        if (params.searchFilters.searchString) {
          queryParams.searchString = params.searchFilters.searchString;
        }
        if (params.searchFilters.bankName) {
          queryParams.bankName = params.searchFilters.bankName;
        }
        if (params.searchFilters.status) {
          queryParams.status = params.searchFilters.status;
        }
        if (params.searchFilters.minBalance !== undefined) {
          queryParams.minBalance = params.searchFilters.minBalance;
        }
        if (params.searchFilters.maxBalance !== undefined) {
          queryParams.maxBalance = params.searchFilters.maxBalance;
        }
      }

      const response = await apiService.get<BankAccountListResponse>('/bankaccount', queryParams);

      if (response.value) {
        set({
          accounts: response.value.accounts,
          balanceTotals: response.value.balanceTotals ?? [],
          totalCount: response.value.totalCount,
          listParameters: response.value.parameters,
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch bank accounts',
        isLoading: false,
      });
    }
  },

  fetchAccountById: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      // First check if we have the account in our local store (from SIBS)
      const localAccount = get().accounts.find((a) => a.id === id);
      if (localAccount) {
        set({
          currentAccount: localAccount,
          isLoading: false,
        });
        return;
      }

      // Fall back to API call
      const response = await apiService.get<BankAccount>(`/bankaccount/${id}`);

      if (response.value) {
        set({
          currentAccount: response.value,
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch bank account',
        isLoading: false,
      });
    }
  },

  // Fetch accounts from SIBS API using consent
  fetchSibsAccounts: async (consent: StoredConsent) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.getSibsAccounts(consent.aspspCde, consent.consentId);

      if (response.value?.accounts) {
        // Fetch balances for each account
        const accountsWithBalances: BankAccount[] = await Promise.all(
          response.value.accounts.map(async (sibsAccount) => {
            try {
              const balances = await get().fetchSibsAccountBalances(
                consent.aspspCde,
                consent.consentId,
                sibsAccount.resourceId
              );
              return sibsAccountToBankAccount(sibsAccount, consent.aspspCde, balances);
            } catch {
              // If balance fetch fails, return account without balance
              return sibsAccountToBankAccount(sibsAccount, consent.aspspCde);
            }
          })
        );

        set({
          accounts: accountsWithBalances,
          balanceTotals: totalsByCurrency(accountsWithBalances),
          totalCount: accountsWithBalances.length,
          sibsAccountsLoaded: true,
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch SIBS accounts',
        isLoading: false,
      });
    }
  },

  // Fetch balances for a specific account
  fetchSibsAccountBalances: async (aspspCde: string, consentId: string, accountId: string): Promise<SibsBalance[]> => {
    try {
      const response = await apiService.getSibsBalances(aspspCde, consentId, accountId);
      return response.value?.balances || [];
    } catch {
      return [];
    }
  },

  createAccount: async (request: CreateBankAccountRequest): Promise<CreateConsentResponse> => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.post<CreateConsentResponse>('/bankaccount', request);

      if (!response.value) {
        throw new Error('No consent response received');
      }

      set({ isLoading: false });

      return response.value;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create bank account',
        isLoading: false,
      });
      throw error;
    }
  },

  updateAccount: async (id: number, request: UpdateBankAccountRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.put<BankAccount>(`/bankaccount/${id}`, request);

      // Reflect the updated account immediately (name + lusopayCredentialsSet) on the detail page.
      if (response.value) {
        set({ currentAccount: response.value });
      }

      // Refresh the list as well.
      await get().fetchAccounts();

      set({ isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update bank account',
        isLoading: false,
      });
      throw error;
    }
  },

  getDeletePreview: async (id: number) => {
    try {
      const response = await apiService.get<BankAccountDeletePreview>(`/bankaccount/${id}/delete-preview`);
      return response.value ?? null;
    } catch {
      return null;
    }
  },

  deleteAccount: async (id: number, deleteMovements = false) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.delete(`/bankaccount/${id}?deleteMovements=${deleteMovements}`);

      // Refresh the list after deletion
      await get().fetchAccounts();

      set({ isLoading: false, currentAccount: null });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete bank account',
        isLoading: false,
      });
      throw error;
    }
  },

  syncAccount: async (id: number) => {
    set((state) => ({ syncingAccountIds: [...state.syncingAccountIds, id] }));
    try {
      await apiService.syncAccount(id, (status) =>
        set((state) => ({ syncProgress: { ...state.syncProgress, [id]: status.transactionsFound } })));
      await get().fetchAccounts();
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to sync account' });
      // A failed sync records its reason on the account: show it.
      await get().fetchAccounts().catch(() => undefined);
    } finally {
      set((state) => {
        const syncProgress = { ...state.syncProgress };
        delete syncProgress[id];
        return {
          syncingAccountIds: state.syncingAccountIds.filter((x) => x !== id),
          syncProgress,
        };
      });
    }
  },

  initiateConsent: async (id: number): Promise<string | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.post<CreateConsentResponse>(`/bankaccount/${id}/consent`);
      set({ isLoading: false });
      // Remember which account we're consenting so the callback page can finalize by
      // ConsentId even if SIBS redirects back with an error (and no accountId param).
      try {
        sessionStorage.setItem(
          'pendingConsent',
          JSON.stringify({ accountId: id, consentId: response.value?.consentId ?? null }),
        );
      } catch { /* sessionStorage unavailable — finalize falls back to URL params */ }
      // The bank already holds the stored consent as approved: no SCA, just finalize and sync it.
      if (!response.value?.scaRedirectUrl && response.value?.consentStatus?.toLowerCase() === 'valid') {
        return `/consent/callback?accountId=${id}`;
      }
      return response.value?.scaRedirectUrl ?? null;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to initiate consent',
        isLoading: false,
      });
      return null;
    }
  },

  // Finalize (acquire) consent by the account's stored ConsentId — decoupled from the
  // SIBS bank-redirect callback. Returns whether the account is now connected.
  finalizeConsent: async (id: number) => {
    const response = await apiService.finalizeConsent(id);
    return response.value ?? null;
  },

  removeConsent: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.delete(`/bankaccount/${id}/consent`);

      // Update the current account to reflect consent removal
      const currentAccount = get().currentAccount;
      if (currentAccount && currentAccount.id === id) {
        set({
          currentAccount: {
            ...currentAccount,
            consentExpiresAt: '0001-01-01T00:00:00',
          },
        });
      }

      // Update the account in the accounts list as well
      set((state) => ({
        accounts: state.accounts.map((account) =>
          account.id === id
            ? { ...account, consentExpiresAt: '0001-01-01T00:00:00' }
            : account
        ),
        isLoading: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to remove consent',
        isLoading: false,
      });
      throw error;
    }
  },

  // SYNCHRONOUS ACTIONS
  setSearchFilters: (filters: BankAccountSearchFilter) => {
    set((state) => ({
      listParameters: {
        ...state.listParameters,
        searchFilters: filters,
        currentPage: 1,
        initial: false,
      },
    }));
  },

  setCurrentPage: (page: number) => {
    set((state) => ({
      listParameters: {
        ...state.listParameters,
        currentPage: page,
      },
    }));
  },

  setSortBy: (sortBy: string, ascending: boolean) => {
    set((state) => ({
      listParameters: {
        ...state.listParameters,
        sortByParameter: sortBy,
        sortAscending: ascending,
        currentPage: 1,
      },
    }));
  },

  clearError: () => set({ error: null }),

  setCurrentAccount: (account: BankAccount | null) => set({ currentAccount: account }),
}));
